import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import ConstructionPlanningInterface from './components/ConstructionPlanningInterface';
import ProjectDetailsPage from './components/ProjectDetailsPage';
import TasksView from './components/tasks/TasksView';
import AppSidebar, { type SidebarView } from './components/shared/AppSidebar';
import LoginPage from './components/LoginPage';
import AccessManagementPage from './components/access/AccessManagementPage';
import { api } from './lib/api';
import { useAuth } from './lib/auth';
import './App.css';

type ThemeType = 'light' | 'dark';

interface Project {
  id: string;
  name: string;
  code?: string;
}

function App() {
  const { loading: authLoading, user, can } = useAuth();
  const [currentView, setCurrentView] = useState<SidebarView>('planning');
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  // Theme state - check localStorage or system preference
  const [theme, setTheme] = useState<ThemeType>(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved as ThemeType;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Apply theme class to document
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => (prev === 'light' ? 'dark' : 'light'));
  };

  useEffect(() => {
    // The API rejects anonymous requests, so there is nothing to fetch until
    // there is a session. Re-runs on sign-in.
    if (!user) {
      setProjects([]);
      setSelectedProjectId(null);
      return;
    }
    const loadProjects = async () => {
      try {
        const projectsData = await api.fetchProjects();
        setProjects(projectsData);
        if (projectsData.length > 0) {
          const haritaProject = projectsData.find((p: Project) => p.name === 'Harita');
          setSelectedProjectId((haritaProject || projectsData[0]).id);
        }
      } catch (error) {
        console.error('Failed to load projects:', error);
      }
    };
    loadProjects();
  }, [user]);

  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId);
  };

  // Navigation between the three top-level views is now driven by the
  // persistent <AppSidebar> on the left rail. Views no longer ship their
  // own switcher in the header.
  const handleSidebarNavigate = (view: SidebarView) => {
    // Guard against landing on Project Details with no project selected.
    if (view === 'project-details' && !selectedProjectId) return;
    setCurrentView(view);
  };

  // Wait for the stored token to be validated before deciding what to show,
  // otherwise a signed-in user sees the login screen flash on every reload.
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-slate-900">
        <Loader2 size={24} className="animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  return (
    <div className="App flex h-screen w-full bg-gray-100 dark:bg-slate-900 transition-colors duration-200 overflow-hidden">
      <AppSidebar
        current={currentView}
        onNavigate={handleSidebarNavigate}
        theme={theme}
        onToggleTheme={toggleTheme}
        disableProjectDetails={!selectedProjectId}
        showAccess={can('users.manage')}
      />

      <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
        {currentView === 'planning' && (
          <ConstructionPlanningInterface
            theme={theme}
          />
        )}
        {currentView === 'project-details' && selectedProjectId && (
          <ProjectDetailsPage
            projectId={selectedProjectId}
            projects={projects}
            onProjectChange={handleProjectChange}
            theme={theme}
          />
        )}
        {currentView === 'tasks' && (
          <TasksView
            projects={projects}
            selectedProjectId={selectedProjectId}
            onProjectChange={handleProjectChange}
            theme={theme}
          />
        )}
        {/* Permission re-checked here, not just on the nav item: hiding the
            rail button alone would leave the view reachable by state. */}
        {currentView === 'access' && can('users.manage') && (
          <AccessManagementPage projects={projects} />
        )}
      </div>
    </div>
  );
}

export default App;
